import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  type DropdownPos,
  dropdownPanelStyle,
  useDropdownPosition,
} from "../ui/dropdown-hooks";
import {
  focusEnterTarget,
  focusNextTabCell,
  focusPrevTabCell,
} from "../../utils/tabNavigation";
import {
  CELL_INPUT_SELECT,
  CELL_INPUT_SELECT_FOCUS,
  CELL_INPUT_DISABLED,
} from "./styles";

const DROPDOWN_DESIRED_HEIGHT = 240;
const DROPDOWN_MIN_WIDTH = 160;
/** Keys that open a closed panel instead of acting on the highlight. */
const OPENING_KEYS = new Set(["ArrowDown", "ArrowUp", "Enter"]);

interface SelectOption {
  value: string;
  label: string;
}

interface InlineCustomFieldSelectProps {
  /** Custom-field key — only used to build the test id. */
  fieldKey: string;
  /** Current value; `""` means "no value" and maps to the clear row. */
  value: string;
  options: SelectOption[];
  /** Receives `""` when the user picks the clear row. */
  onChange: (value: string) => void;
  disabled: boolean;
  /** Shown in the trigger when the field has no value. */
  placeholder: string;
  /** Label of the leading "no value" row. */
  clearLabel: string;
}

/**
 * Select control for an inline custom-field column.
 *
 * Deliberately NOT a native `<select>`: the browser draws that popup itself
 * (native macOS/Windows chrome, unstyleable list, own font and highlight), so
 * a single custom-field column looked foreign next to every other recap cell.
 * This is the same portal-dropdown construction `UnitCell` uses — trigger
 * styled with the shared `CELL_INPUT_SELECT`, panel rendered into `document.body`
 * and positioned by `useDropdownPosition` (flips above the trigger and clamps
 * its height near the viewport edge, which a table-clipped absolute panel
 * cannot do).
 *
 * Keyboard contract matches the rest of the grid: Arrow keys open/move,
 * Enter commits and steps down a row, Tab commits and moves to the next cell,
 * Escape closes without committing. The trigger carries `data-tab-cell` so
 * `tabNavigation` treats it as an ordinary editable cell.
 */
export function InlineCustomFieldSelect({
  fieldKey,
  value,
  options,
  onChange,
  disabled,
  placeholder,
  clearLabel,
}: Readonly<InlineCustomFieldSelectProps>) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // -1 = nothing highlighted, the state a row whose stored value is not in
  // `options` opens in. `commit(-1)` is a no-op, which is what keeps Tab/Enter
  // from overwriting that value (see `selectedIndex`).
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Hover is styling ONLY and deliberately does not touch `highlightedIndex`.
  // The mouse must never move the commit target: pointing at a row is not
  // choosing it (a click is), and the -1 sentinel above is what protects an
  // unknown stored value. Folding hover into `highlightedIndex` defeated that
  // guard — drifting the pointer across the clear row at index 0 turned a
  // subsequent Tab into `commit(0)`, erasing a value the user never selected.
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);

  // The clear row is a real item at index 0 rather than a sentinel value, so
  // arrow-key navigation and `selectedIndex` need no special-casing.
  const items = useMemo<SelectOption[]>(
    () => [
      { value: "", label: clearLabel },
      ...options.filter((o) => !!o.value),
    ],
    [options, clearLabel],
  );

  // Deliberately NOT clamped to 0: a stored value that is no longer in
  // `options` (field redefined, option retired) must stay -1. Clamping it to
  // the clear row made the cell *display* the clear label and — because
  // `openDropdown` seeds the highlight from here — made a bare open+Tab commit
  // `""`, silently erasing a value the user never touched. At -1 nothing is
  // selected, `items[-1]` is undefined and `commit` early-returns, so an
  // unknown value round-trips untouched.
  const selectedIndex = items.findIndex((item) => item.value === value);
  const selectedLabel = value ? (items[selectedIndex]?.label ?? value) : "";

  useDropdownPosition(
    open,
    triggerRef,
    setDropdownPos,
    DROPDOWN_MIN_WIDTH,
    DROPDOWN_DESIRED_HEIGHT,
  );

  const openDropdown = useCallback(() => {
    setHighlightedIndex(selectedIndex);
    setHoveredIndex(-1);
    setOpen(true);
  }, [selectedIndex]);

  const commit = useCallback(
    (index: number) => {
      const item = items[index];
      if (!item) return;
      onChange(item.value);
      setOpen(false);
    },
    [items, onChange],
  );

  // Keyboard-driven highlight moves must follow the scroll; a mouse hover
  // already has the row on screen, so only these call scrollIntoView.
  const moveHighlight = useCallback((next: number) => {
    setHighlightedIndex(next);
    // Keyboard reclaims the highlight from a stale hover, otherwise the row
    // the pointer happens to rest on keeps the styling while Arrow keys move
    // the real (commit-bearing) selection invisibly underneath it.
    setHoveredIndex(-1);
    requestAnimationFrame(() => {
      dropdownRef.current
        ?.querySelector(`[data-option-index="${next}"]`)
        ?.scrollIntoView({ block: "nearest" });
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      // On a closed panel these three keys only open it. Handling that once,
      // up front, keeps the branch below a flat switch — the same guard
      // repeated inside each arm is what pushed this over the
      // cognitive-complexity budget.
      if (!open && OPENING_KEYS.has(e.key)) {
        e.preventDefault();
        openDropdown();
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveHighlight(Math.min(highlightedIndex + 1, items.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          moveHighlight(Math.max(highlightedIndex - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          commit(highlightedIndex);
          // Same as the numeric cells: Enter steps to this column one row down,
          // Shift+Enter jumps to the add-product row.
          focusEnterTarget(e.currentTarget, e.shiftKey);
          break;
        case "Tab": {
          // Commit before leaving, mirroring UnitCell. A panel opened on a
          // value that is no longer in `options` highlights nothing (-1), so
          // this is a no-op there rather than an unrequested clear.
          if (open) commit(highlightedIndex);
          const moved = e.shiftKey
            ? focusPrevTabCell(e.currentTarget)
            : focusNextTabCell(e.currentTarget);
          if (moved) e.preventDefault();
          break;
        }
        case "Escape":
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, openDropdown, highlightedIndex, items.length, moveHighlight, commit],
  );

  // What the user SEES highlighted: the pointer wins while it is over a row,
  // otherwise the keyboard's selection shows through. Only `highlightedIndex`
  // is ever committed, so this stays purely cosmetic.
  const activeIndex = hoveredIndex >= 0 ? hoveredIndex : highlightedIndex;

  const panel =
    open && dropdownPos ? (
      <div
        ref={dropdownRef}
        data-recap-portal
        data-testid={`flow-custom-field-${fieldKey}-options`}
        role="listbox"
        // Focusable programmatically only (-1, never in the tab order): the
        // trigger keeps DOM focus for the whole interaction, so Tab must still
        // step to the next grid cell rather than into the panel.
        tabIndex={-1}
        // Keeps focus on the trigger for the whole interaction: without this
        // the mousedown blurs the button, `onBlur` closes the panel, and the
        // click never lands on an option.
        onMouseDown={(e) => e.preventDefault()}
        // Leaving the panel hands the highlight back to the keyboard rather
        // than stranding it on the last row the pointer crossed.
        onMouseLeave={() => setHoveredIndex(-1)}
        style={{
          position: "fixed",
          zIndex: 9999,
          ...dropdownPanelStyle(dropdownPos),
        }}
        className="overflow-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
      >
        {items.map((item, index) => (
          <div
            key={item.value || "__clear__"}
            data-option-index={index}
            role="option"
            aria-selected={index === selectedIndex}
            tabIndex={-1}
            onClick={() => commit(index)}
            // The trigger owns the keyboard contract (focus never leaves it),
            // so this only fires if a row is focused programmatically — but an
            // element carrying a click handler must still answer the keyboard.
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                commit(index);
              }
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            className={cn(
              "mx-1 cursor-pointer break-words rounded-sm px-2 py-1.5 text-left text-xs",
              index === activeIndex && "bg-accent text-accent-foreground",
              index === selectedIndex && "font-semibold",
              index === 0 && "text-muted-foreground",
            )}
          >
            {item.label}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-tab-cell
        data-testid={`flow-custom-field-${fieldKey}-select`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        title={selectedLabel || placeholder}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else openDropdown();
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={handleKeyDown}
        className={cn(
          CELL_INPUT_SELECT,
          "flex items-center justify-between gap-1 text-left",
          selectedLabel ? "text-foreground" : "text-muted-foreground",
          disabled
            ? cn(CELL_INPUT_DISABLED, "text-foreground")
            : cn("cursor-pointer", CELL_INPUT_SELECT_FOCUS),
        )}
      >
        <span className="truncate">{selectedLabel || placeholder}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
      </button>
      {typeof window !== "undefined" &&
        panel &&
        createPortal(panel, document.body)}
    </div>
  );
}
